/*Contains various utils that were migrated from legacy custom reports/cockpit/aggregated dashboard*/
import { MAX_LIKERT_QUESTIONS, TEST_TYPES } from "../../../constants";
import { GenericObject } from "@/types";
import { countBy, groupBy, keyBy, round, sum } from "lodash-es";
import { DtoEntry, DtoTestSet } from "@/types/dto-types";
import { decodeStringNumberToValuesArray } from "../../../utils";
import { GroupedEntries, StatisticItem } from "./types";

const EMPTY_ARR: any[] = [];

const QUESTION_TYPES_FOR_STATISTICS = [
  TEST_TYPES.DoubleSlider,
  TEST_TYPES.SingleSlider,
  TEST_TYPES.WordCloud,
];

export function getIndex(i: number) {
  return (i + 1).toString();
}

export function createEmptyArray(count: number) {
  return Array(count).fill(undefined);
}

function getValueKeys(value: string, type: string): string[] {
  return type !== TEST_TYPES.MULTI_CHOICE
    ? [value]
    : decodeStringNumberToValuesArray(value);
}

function getRepliesGroupedByQuestion(
  entries: Array<GenericObject>,
  questions: DtoTestSet["items"]
): GroupedEntries {
  return entries.reduce<GroupedEntries>((entriesAcc, entry) => {
    questions.forEach(({ dbColumn, type }) => {
      const valueKeys = getValueKeys(entry[dbColumn], type); //array is needed for multichoice type because of binary encoding

      if (!entriesAcc[dbColumn]) {
        entriesAcc[dbColumn] = {};
      }

      valueKeys.forEach(valueKey => {
        if (!entriesAcc[dbColumn][valueKey]) {
          entriesAcc[dbColumn][valueKey] = 1;
        } else {
          entriesAcc[dbColumn][valueKey] += 1;
        }
      });

      if (!entriesAcc[dbColumn]["sum"]) {
        entriesAcc[dbColumn]["sum"] = 0;
      }

      entriesAcc[dbColumn]["sum"] += 1;
    });
    return entriesAcc;
  }, {});
}

function getQuestionsForTestSet(
  testSet: DtoTestSet,
  testSetEntries: Array<GenericObject>
) {
  const filteredQuestions = testSet.items.filter(question =>
    QUESTION_TYPES_FOR_STATISTICS.includes(question.type)
  );
  const repliesGrouped = getRepliesGroupedByQuestion(
    testSetEntries,
    filteredQuestions
  );

  return filteredQuestions.map((question, qId) => {
    const questionReply: GenericObject<number> =
      repliesGrouped[question.dbColumn];
    const choices = createEmptyArray(question.values as number).map(
      (dummy: undefined, index) => {
        const replyIndex = getIndex(index);
        const count = questionReply[index] || 0;
        const percentage = round((count / questionReply.sum) * 100, 2);

        return {
          id: `choice-${index.toString()}`,
          title: question[`v${replyIndex}` as `v${number}`],
          count,
          percentage,
          replyIndex: parseInt(replyIndex, 10),
        };
      }
    );

    const averageDistribution = choices.reduce((acc, choice) => {
      return acc + choice.count * choice.replyIndex;
    }, 0);

    const totalChoicesSelected = choices.reduce((acc, choice) => {
      return acc + choice.count;
    }, 0);

    const average = round(averageDistribution / questionReply.sum, 2);

    return {
      id: `question-${qId.toString()}`,
      title: question.text,
      choices,
      average,
      totalReplies: questionReply.sum,
      totalChoicesSelected,
      type: question.type,
    } as StatisticItem;
  });
}

export function getStatisticData(
  testSets: Array<DtoTestSet>,
  entries: Array<GenericObject>
) {
  if (testSets.length && entries.length) {
    const entriesByTestSet = groupBy(entries, "testSetId");
    const testSetsMap = keyBy(testSets, "_id");

    return Object.keys(testSetsMap).reduce<StatisticItem[]>((acc, tsId) => {
      const questionsForTestSet = getQuestionsForTestSet(
        testSetsMap[tsId],
        entriesByTestSet[tsId]
      );
      return [...acc, ...questionsForTestSet];
    }, []);
  }

  return EMPTY_ARR;
}

export function getQuestionChoices(testSets: DtoTestSet[]) {
  const answers: { key: string; name: string }[] = [];

  if (testSets.length) {
    const [ts1] = testSets;
    const valuesCount = ts1.items[0]?.values ?? 0;
    for (let i = 1; i <= valuesCount; i++) {
      const key = `v${i}` as `v${number}`;
      answers.push({
        key,
        name: ts1.items[0][key],
      });
    }
  }

  return answers;
}

export function getQuestionsData(
  testSets: DtoTestSet[],
  entries: DtoEntry[],
  isIndividualReport: boolean
) {
  const choices = getQuestionChoices(testSets);
  const entriesPerTestSet = entries.reduce<any>((acc, entry) => {
    if (!acc[entry.testSetId]) {
      acc[entry.testSetId] = {};
    }

    for (let i = 1; i <= MAX_LIKERT_QUESTIONS; i++) {
      const key = `V${i}` as `V${number}`;
      if (!acc[entry.testSetId][key]) {
        acc[entry.testSetId][key] = [];
      }
      acc[entry.testSetId][key].push(entry[key]);
    }

    return acc;
  }, {});

  return {
    choices,
    questions: testSets.reduce<any>((acc, testSet) => {
      testSet.items
        .filter(item => QUESTION_TYPES_FOR_STATISTICS.includes(item.type))
        .forEach(qItem => {
          const tsEntries = entriesPerTestSet[testSet._id];
          const questionEntries: number[] = (tsEntries?.[qItem.dbColumn] || [])
            .filter((num?: string) => num !== undefined)
            .map((num: string) => parseFloat(num));
          const entries: GenericObject<number> = {
            V1: 0,
            V2: 0,
            V3: 0,
            V4: 0,
            V5: 0,
            V6: 0,
          };

          let average = 0;

          if (isIndividualReport) {
            Object.keys(entries).forEach(key => {
              const value = questionEntries[0];
              if (key === `V${value}`) {
                entries[key] = value;
              }
            });
          } else {
            const questionEntriesCountBy = countBy<number>(questionEntries);
            const allQuestionsSum = sum(questionEntries);
            average = allQuestionsSum / questionEntries.length;

            Object.keys(questionEntriesCountBy).forEach(key => {
              entries[`V${key}`] =
                (questionEntriesCountBy[key] / questionEntries.length) * 100;
            });
          }

          acc.push({
            testSetId: testSet._id,
            text: qItem.text,
            dbColumn: qItem.dbColumn,
            entries,
            questionEntries,
            average,
          });
        });

      return acc;
    }, []),
  };
}
